<template lang="pug">
  v-container
    illustration-state(
      :illustration="illustration"
      title="Recevez votre code par SMS"
      body="Ce code aléatoire à usage unique permet d'identifier votre téléphone.")
    v-row
      v-col(:cols="12")
        input-phone(v-model="phoneNumber")
        input-captcha(
          v-if="phoneNumber"
          v-model="captcha"
          :phone-number="phoneNumber")
</template>

<script>
import IllustrationState from '@iez/components/illustration-state'
import InputPhone from '@iez/components/inputs/phone/'
import InputCaptcha from '@iez/components/inputs/captcha/'
import illustration from '@iez/illustrations/MessageSent'
import { inject, reactive, toRefs, onMounted, watch, ref, computed } from '@vue/composition-api'
import { page } from '@iez/core'
import router from '@iez/core/router'
import waitForActively from '@iez/lib/wait-for-actively/'
import waitFor from '@iez/lib/wait-for/'
import { prioritiesGlobal } from '@iez/core/app/set-priority/'
import data from '@iez/lib/data/'
import firebase from 'firebase/app'
import 'firebase/auth'
import initNativeCallback from '@iez/lib/init-native-callback/'

const auth = firebase.auth()

export const route = {
  name: 'Authentication-PhoneNumber',
  path: '/authentication/phone-number'
}

export default {
  ...route,

  components: {
    InputPhone,
    InputCaptcha,
    IllustrationState
  },

  props: {
    priority: Number
  },

  setup (props, { emit }) {
    const routeState = inject(`setup${route.name}`)({
      confirmationResult: null,
      phoneNumber: ''
    })

    const state = reactive({
      phoneNumber: ``,
      captcha: null,
      confirmationResult: null,
      loading: false,
      redirecting: false
    })

    const topBar = page.topBar.set({
      title: 'Authentification',
      route,
      steps: {
        current: 1,
        count: 2
      }
    })

    const { callback: nativeSignInWithPhoneNumber } = initNativeCallback({
      name: `signInWithPhoneNumber`
    })

    const openCaptchaModalToSendSms = async () => {
      const confirmationResult = window.ReactNativeWebView
        ? await nativeSignInWithPhoneNumber({ phoneNumber: state.phoneNumber })
        : await auth.signInWithPhoneNumber(state.phoneNumber, state.captcha)
          .catch(error => {
            alert(error)
            state.captcha = null
          })
      if (!window.ReactNativeWebView) {
        state.captcha.render()
      }
      if (confirmationResult) {
        state.redirecting = true
        if (confirmationResult?.id) {
          await closeCaptchaModal()
        }
        state.captcha.clear()

        routeState.confirmationResult = confirmationResult
        routeState.phoneNumber = state.phoneNumber

        const user = data.get({
          root: `users`,
          one: true,
          fields: [
            `id`,
            `phoneNumber`
          ],
          request: {
            where: {
              phoneNumber: {
                _eq: state.phoneNumber
              }
            }
          },
          isCalledFromComponent: false
        })
        await waitFor({
          dependencies: () => user.value !== undefined
        })
        state.loading = false
        router.push({
          name: 'Authentication-Otp',
          params: {
            isNewUser: !user.value?.id
          }
        })
      }
    }

    const handleCaptchaModalCloseEvent = async () => {
      const iframe = await waitForActively({
        predicate: () => document.querySelector(`iframe[title="recaptcha challenge"]`)
      })
      const modal = iframe.parentNode.parentNode
      await waitForActively({
        predicate: () => modal.style.visibility === 'visible'
      })
      await waitForActively({
        predicate: () => modal.style.visibility === 'hidden'
      })
      setTimeout(async () => {
        if (!state.redirecting) {
          await closeCaptchaModal()
          state.captcha.reset()
          state.loading = false
        }
      }, 600)
    }

    const closeCaptchaModal = async () => {
      const iframe = await waitForActively({
        predicate: () => document.querySelector(`iframe[title="recaptcha challenge"]`)
      })
      const modal = iframe.parentNode.parentNode
      modal.remove()
    }

    page.bottomAction.set(
      () => state.phoneNumber && state.captcha && !state.confirmationResult
        ? ({
          action: {
            label: `Recevoir un code`,
            isLoading: computed(() => state.loading),
            onClick: () => {
              state.loading = true
              openCaptchaModalToSendSms()
              handleCaptchaModalCloseEvent()
            }
          }
        }) : null
    )

    return {
      ...toRefs(state),
      illustration
    }
  }
}
</script>
